import { Dialog } from '@material-ui/core';
import { ArrowBack, ArrowForward, Close } from '@material-ui/icons';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

// The "What's new" dialog: one feature at a time, each with a screenshot, a
// short bulleted list and, where the feature has one, a "Show me" tour. Esc,
// the backdrop, the close button and "Done" all close it.

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-1 border-0 cursor-pointer font-semibold text-sm px-4 py-2 rounded-xl whitespace-nowrap transition-colors duration-150 active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2';
const PRIMARY = `${BUTTON_BASE} bg-indigo-600 hover:bg-indigo-700 text-white`;
const SECONDARY = `${BUTTON_BASE} bg-white hover:bg-slate-50 text-slate-700 border border-solid border-slate-300 shadow-sm`;
const GHOST = `${BUTTON_BASE} bg-transparent hover:bg-slate-100 text-slate-600 px-3`;

const Skeleton = () => (
  <div className="p-6 flex flex-col gap-4 animate-pulse" role="status" aria-label="Loading what's new">
    <div className="w-full aspect-[16/9] bg-slate-100 rounded-2xl" />
    <div className="h-5 w-2/3 bg-slate-100 rounded" />
    <div className="h-3 w-full bg-slate-100 rounded" />
    <div className="h-3 w-5/6 bg-slate-100 rounded" />
  </div>
);

const formatRelease = releasedAt => {
  const date = new Date(`${releasedAt}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
};

export function WhatsNewDialog({ open, loading, error, data, mode, onClose, onRetry, onShowMe, onOpenFaq }) {
  const items = data?.items || [];
  const [index, setIndex] = useState(0);
  // Images that failed to load, so a missing file collapses instead of
  // leaving an empty frame with alt text in it.
  const [brokenImages, setBrokenImages] = useState({});

  // A fresh open always starts on the newest entry.
  useEffect(() => {
    if (open) setIndex(0);
  }, [open, data]);

  const item = items[Math.min(index, items.length - 1)];
  const isLast = index >= items.length - 1;
  const subtitle =
    mode === 'all'
      ? 'Recent updates to your dashboard'
      : items.length === 1
        ? 'One new feature since your last visit'
        : `${data?.newCount ?? items.length} new features since your last visit`;

  // Passing onKeyDown to a Material-UI v4 Dialog replaces the Modal's own
  // handler, and that is the one that closes on Escape, so it is handled here.
  const onKeyDown = event => {
    if (event.key === 'Escape') onClose();
    if (event.key === 'ArrowRight' && !isLast) setIndex(i => i + 1);
    if (event.key === 'ArrowLeft' && index > 0) setIndex(i => i - 1);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onKeyDown={onKeyDown}
      maxWidth="sm"
      fullWidth
      aria-labelledby="whats-new-title"
      PaperProps={{ style: { borderRadius: 24 }, 'data-testid': 'whats-new-dialog' }}>
      <div className="flex items-start justify-between gap-4 px-6 pt-5">
        <div className="min-w-0">
          <h2 id="whats-new-title" className="text-xl font-black text-slate-900 m-0">
            What&apos;s new
          </h2>
          {!loading && !error && items.length > 0 && <p className="text-sm text-slate-500 m-0 mt-1">{subtitle}</p>}
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className={`${GHOST} !p-2 -mr-2`}>
          <Close fontSize="small" />
        </button>
      </div>

      {loading && <Skeleton />}

      {!loading && error && (
        <div className="px-6 py-8 flex flex-col items-start gap-3">
          <p className="text-sm text-slate-600 m-0">We couldn&apos;t load the latest updates.</p>
          <button type="button" className={SECONDARY} onClick={onRetry}>
            Try again
          </button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="px-6 py-8 flex flex-col items-start gap-3">
          <p className="text-base font-bold text-slate-900 m-0">You&apos;re all caught up</p>
          <p className="text-sm text-slate-500 m-0">
            Nothing new since your last visit. Questions about getting paid, tipped or reviewed are answered in the FAQ.
          </p>
          <button type="button" className={SECONDARY} onClick={onOpenFaq}>
            Open the FAQ
          </button>
        </div>
      )}

      {!loading && !error && item && (
        <>
          <div key={item.id} className="px-6 pt-4" data-whats-new-item={item.id}>
            {item.imageUrl && !brokenImages[item.imageUrl] && (
              <div className="w-full aspect-[16/9] bg-indigo-50 rounded-2xl overflow-hidden">
                <img
                  src={item.imageUrl}
                  alt={item.imageAlt || ''}
                  className="w-full h-full object-contain block"
                  onError={() => setBrokenImages(prev => ({ ...prev, [item.imageUrl]: true }))}
                />
              </div>
            )}
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <h3 className="text-lg font-black text-slate-900 m-0">{item.title}</h3>
              {mode === 'all' && item.isNew && (
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-lg bg-indigo-50 text-indigo-700 border border-solid border-indigo-100">
                  New
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400 font-semibold m-0 mt-1">{formatRelease(item.releasedAt)}</p>
            <p className="text-sm text-slate-600 leading-relaxed m-0 mt-2">{item.summary}</p>
            {item.bullets?.length > 0 && (
              <ul className="m-0 mt-3 pl-5 text-sm text-slate-700 leading-relaxed list-disc marker:text-indigo-500">
                {item.bullets.map(bullet => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 px-6 py-5 mt-2 flex-wrap">
            <span className="text-xs text-slate-400 font-semibold" aria-live="polite">
              {items.length > 1 ? `${index + 1} of ${items.length}` : ''}
            </span>
            <div className="flex items-center gap-2 ml-auto">
              {index > 0 && (
                <button type="button" className={GHOST} onClick={() => setIndex(i => i - 1)} aria-label="Previous feature">
                  <ArrowBack fontSize="small" />
                </button>
              )}
              {item.tour?.length > 0 && (
                <button type="button" className={SECONDARY} onClick={() => onShowMe(item)}>
                  Show me
                </button>
              )}
              {isLast ? (
                <button type="button" className={PRIMARY} onClick={onClose}>
                  Done
                </button>
              ) : (
                <button type="button" className={PRIMARY} onClick={() => setIndex(i => i + 1)}>
                  Next <ArrowForward fontSize="small" />
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
}

WhatsNewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  data: PropTypes.object,
  mode: PropTypes.oneOf(['new', 'all']),
  onClose: PropTypes.func.isRequired,
  onRetry: PropTypes.func,
  onShowMe: PropTypes.func.isRequired,
  onOpenFaq: PropTypes.func.isRequired
};

export default WhatsNewDialog;
